import { z } from 'zod';
import { UserType } from '../common/enums';
import { AllCharLabelSchema, HumanFullNameSchema } from '../common/schemas';
import {
  DBLessonTemplateCourseLessonIdSchema,
  LessonTemplateCourseSchema,
  LessonTemplateLessonIdSchema,
} from '../lessonTemplates/schemas';
import {
  DBPermissionGroupGroupIdSchema,
  PermissionGroupGroupIdSchema,
} from '../permissionGroups/schemas';
import {
  DBStreamCourseStreamIdSchema,
  StreamCourseSchema,
  StreamStreamIdSchema,
} from '../streams/schemas';
import { UserIdSchema } from '../users/schemas';
import { PermissionSource, PermissionType } from './enums';

/**
 * Shared & Helper Schemas
 */
export const PermissionTypeSchema = z.enum(PermissionType).openapi({
  description: 'Type of permission by related grouping',
  example: PermissionType.Video,
});

export const PermissionSourceSchema = z.enum(PermissionSource);

/**
 * DB Schemas
 */

export const DBPermissionPartitionSchema = z
  .string()
  .regex(new RegExp(`^${PermissionType.Base}(_[A-Z]{3})?$`))
  .min(1)
  .max(256)
  .openapi({
    description: 'Prefix grouping of related permission tags',
    example: 'CPM_VID',
  });

export const DBPermissionPermissionIdSchema = z
  .string()
  .regex(/^[A-Z]{3}(_[A-Z0-9]+)*$/)
  .min(1)
  .max(256);

export const DBPermissionLabelSchema = AllCharLabelSchema.min(1).max(256).openapi({
  description: 'Human-readable label for the permission',
  example: 'Chemistry / Module 1 / L1 HW sols notes',
});

export const DBPermissionUsersSchema = z.record(
  z.lazy(() => UserIdSchema),
  z
    .object({
      name: HumanFullNameSchema,
      type: z.enum(UserType),
    })
    .strict()
);

export const DBPermissionGroupsSchema = z.record(
  z.lazy(() => DBPermissionGroupGroupIdSchema),
  z.object({}).strict() // empty object as value
);

export const DBPermissionStreamsSchema = z.record(
  z.lazy(() => DBStreamCourseStreamIdSchema),
  z.object({}).strict() // empty object as value
);

export const DBPermissionLessonTemplatesSchema = z.record(
  z.lazy(() => DBLessonTemplateCourseLessonIdSchema),
  z.object({}).strict() // empty object as value
);

export const DBPermissionSchema = z.object({
  partition: DBPermissionPartitionSchema,
  permissionId: DBPermissionPermissionIdSchema,
  label: DBPermissionLabelSchema,
  // .optional() indicates to ElectroDB that keys of these dynamic fields may be deleted
  users: DBPermissionUsersSchema.optional(),
  groups: DBPermissionGroupsSchema.optional(),
  streams: DBPermissionStreamsSchema.optional(),
  lessonTemplates: DBPermissionLessonTemplatesSchema.optional(),
});

/**
 * Client Schemas
 */

export const PermissionValueSchema = DBPermissionPermissionIdSchema;
export const PermissionPartitionSchema = DBPermissionPartitionSchema;
export const PermissionLabelSchema = DBPermissionLabelSchema;
export const PermissionUsersSchema = z.array(
  z.object({
    userId: z.lazy(() => UserIdSchema),
    name: HumanFullNameSchema,
    type: z.enum(UserType),
  })
);
export const PermissionGroupsSchema = z.array(z.lazy(() => PermissionGroupGroupIdSchema));
export const PermissionStreamsSchema = z.array(
  z.object({
    course: z.lazy(() => StreamCourseSchema),
    streamId: z.lazy(() => StreamStreamIdSchema),
  })
);
export const PermissionLessonTemplatesSchema = z.array(
  z.object({
    course: z.lazy(() => LessonTemplateCourseSchema),
    lessonId: z.lazy(() => LessonTemplateLessonIdSchema),
  })
);

export const PermissionSchema = z.object({
  value: PermissionValueSchema.meta({ title: 'Tag' }),
  partition: PermissionPartitionSchema,
  label: PermissionLabelSchema.meta({ title: 'Label' }),
  users: PermissionUsersSchema.default([]).meta({ title: 'Users' }),
  groups: PermissionGroupsSchema.default([]).meta({ title: 'Groups' }),
  streams: PermissionStreamsSchema.default([]),
  lessonTemplates: PermissionLessonTemplatesSchema.default([]),
});
